import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Avatar, TextField, Button,
  Grid, Chip, Divider, Alert, Collapse, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Tooltip, FormControl, InputLabel,
  Select, MenuItem, InputAdornment, FormHelperText, Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Visibility, VisibilityOff,
  CheckBox, CheckBoxOutlineBlank,
  AdminPanelSettings as AdminIcon,
  Search as SearchIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  CameraAlt as CameraAltIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { getAvatarUrl } from '../services/api';
import { userService, User, CreateUserData, UpdateUserData } from '../services/userService';
import { userActivityService, UserActivity } from '../services/userActivityService';

// ── helpers ──────────────────────────────────────────────────────────────────

const SUPER_ADMINS = ['admin', 'SasiriOCL'];

const ROLE_COLOR: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  admin: 'error', manager: 'warning', technician: 'info', machine: 'success', viewer: 'default',
};

const ROLE_BG: Record<string, string> = {
  admin: '#c62828', manager: '#e65100', technician: '#1565c0',
  machine: '#2e7d32', viewer: '#616161',
};

const SYSTEM_LABELS: Record<string, string> = {
  'spare-parts': 'Spare Parts',
  'pr-management': 'PR Management',
  'printing-process': 'Printing Process',
  'machine-efficiency': 'Machine Efficiency',
  'pfp-trolly': 'PFP Trolly',
  'waste-management': 'Waste Management',
  'dye-stock-management': 'Dye Stock Management',
  'reprocessing-management': 'Reprocessing Management',
  'maintenance-management': 'Maintenance',
  'quality-management': 'Quality Management',
  'energy-management': 'Energy Management',
  'iot-system': 'Wireless Sensor Network',
};

const ALL_SYSTEMS = Object.keys(SYSTEM_LABELS);

function getRoleDisplayName(role: string) {
  switch (role) {
    case 'manager': return 'System manager';
    default: return role;
  }
}

function getRoleDescription(role: string) {
  switch (role) {
    case 'admin': return 'Full system access — can manage all users and settings';
    case 'manager': return 'System manager — can view and manage inventory, transactions, and reports';
    case 'technician': return 'Can view parts, create transactions, and log maintenance';
    case 'machine': return 'Machine operator — can only access assigned machine data';
    case 'viewer': return 'Read-only access to inventory and reports';
    default: return '';
  }
}

// ── Tab panel ─────────────────────────────────────────────────────────────────

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Main component ────────────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const { user: currentUser, updateUser } = useAuthStore();
  const isSuperAdmin = currentUser ? SUPER_ADMINS.includes(currentUser.username) : false;
  const isAdmin = currentUser?.role === 'admin';

  const [tab, setTab] = useState(0);

  // ── Profile state ──────────────────────────────────────────────────────────
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileData, setProfileData] = useState({ full_name: '', email: '', designation: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Avatar upload state
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = React.useRef<HTMLInputElement>(null);

  // Change password state
  const [pwData, setPwData] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setProfileData({ full_name: currentUser.full_name, email: currentUser.email, designation: currentUser.designation || '' });
    }
  }, [currentUser]);

  const handleProfileSave = async () => {
    if (!profileData.full_name.trim() || !profileData.email.trim()) {
      setProfileMsg({ type: 'error', text: 'Full name and email are required' });
      return;
    }
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const { user } = await authService.updateProfile(profileData);
      updateUser(user as any);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully' });
      setProfileEditing(false);
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update profile' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setProfileMsg({ type: 'error', text: 'Image must be smaller than 5 MB' });
      return;
    }
    setAvatarUploading(true);
    setProfileMsg(null);
    try {
      const { user } = await authService.uploadProfilePicture(file);
      updateUser(user as any);
      setProfileMsg({ type: 'success', text: 'Profile picture updated' });
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to upload image' });
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarUploading(true);
    setProfileMsg(null);
    try {
      const { user } = await authService.deleteProfilePicture();
      updateUser(user as any);
      setProfileMsg({ type: 'success', text: 'Profile picture removed' });
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to remove picture' });
    } finally {
      setAvatarUploading(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!pwData.current || !pwData.next || !pwData.confirm) {
      setPwMsg({ type: 'error', text: 'All fields are required' });
      return;
    }
    if (pwData.next !== pwData.confirm) {
      setPwMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (pwData.next.length < 6) {
      setPwMsg({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    setPwSaving(true);
    setPwMsg(null);
    try {
      await authService.changePassword(pwData.current, pwData.next);
      setPwMsg({ type: 'success', text: 'Password changed successfully' });
      setPwData({ current: '', next: '', confirm: '' });
    } catch (err: any) {
      setPwMsg({ type: 'error', text: err.response?.data?.error || 'Failed to change password' });
    } finally {
      setPwSaving(false);
    }
  };

  // ── User Management state ──────────────────────────────────────────────────
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [umError, setUmError] = useState<string | null>(null);
  const [umSuccess, setUmSuccess] = useState<string | null>(null);

  // Edit/Create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateUserData & UpdateUserData>>({
    username: '', email: '', password: '', full_name: '', designation: '',
    role: 'viewer', assigned_machine: '', is_active: 1, allowed_systems: [], pr_report_frequency: 'none', stock_report_frequency: 'none',
  });
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // Activity dialog
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityUser, setActivityUser] = useState<User | null>(null);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [activitiesTotal, setActivitiesTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRows, setActivityRows] = useState(25);
  const [activityLoading, setActivityLoading] = useState(false);

  const loadUsers = async () => {
    setUsersLoading(true);
    setUmError(null);
    try {
      const res = await userService.getAll();
      const sorted = (res.users || []).sort((a: User, b: User) => {
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        return a.username.localeCompare(b.username);
      });
      setUsers(sorted);
    } catch {
      setUmError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && tab === 1) loadUsers();
  }, [isAdmin, tab]);

  const filteredUsers = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return u.username.toLowerCase().includes(q) ||
        u.full_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q);
    }
    return true;
  });

  const openCreateDialog = () => {
    setSelectedUser(null);
    setFormData({ username: '', email: '', password: '', full_name: '', designation: '', role: 'viewer', assigned_machine: '', is_active: 1, allowed_systems: [], pr_report_frequency: 'none', stock_report_frequency: 'none' });
    setFormError(null);
    setFormSuccess(null);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEditDialog = (u: User) => {
    if (u.role === 'admin' && !isSuperAdmin) {
      setUmError('Only super admins can modify admin accounts');
      setTimeout(() => setUmError(null), 3000);
      return;
    }
    setSelectedUser(u);
    setFormData({
      username: u.username, email: u.email, full_name: u.full_name,
      designation: u.designation || '',
      pr_report_frequency: u.pr_report_frequency || 'none',
      stock_report_frequency: u.stock_report_frequency || 'none',
      role: u.role, assigned_machine: u.assigned_machine || '',
      is_active: u.is_active, allowed_systems: u.allowed_systems || [], password: '',
    });
    setFormError(null);
    setFormSuccess(null);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleFormSave = async () => {
    setFormError(null);
    if (!formData.username?.trim() || !formData.email?.trim() || !formData.full_name?.trim()) {
      setFormError('Username, email, and full name are required');
      return;
    }
    if (!selectedUser && !formData.password?.trim()) {
      setFormError('Password is required for new users');
      return;
    }
    if (formData.password && formData.password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }
    setFormSaving(true);
    try {
      if (selectedUser) {
        const updateData: UpdateUserData = {
          username: formData.username, email: formData.email, full_name: formData.full_name,
          designation: formData.designation || undefined,
          pr_report_frequency: formData.pr_report_frequency as any,
          stock_report_frequency: formData.stock_report_frequency as any,
          role: formData.role, assigned_machine: formData.assigned_machine || undefined,
          is_active: formData.is_active, allowed_systems: formData.allowed_systems,
        };
        if (formData.password?.trim()) updateData.password = formData.password;
        await userService.update(selectedUser.id, updateData);
        setFormSuccess('User updated successfully');
      } else {
        await userService.create({
          username: formData.username!, email: formData.email!, password: formData.password!,
          full_name: formData.full_name!, designation: formData.designation || undefined,
          pr_report_frequency: formData.pr_report_frequency as any,
          stock_report_frequency: formData.stock_report_frequency as any,
          role: formData.role as any,
          assigned_machine: formData.assigned_machine || undefined,
          allowed_systems: formData.allowed_systems,
        });
        setFormSuccess('User created successfully');
      }
      setTimeout(() => { setDialogOpen(false); loadUsers(); }, 1200);
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (u: User) => {
    if (u.role === 'admin' && !isSuperAdmin) {
      setUmError('Only super admins can delete admin accounts');
      setTimeout(() => setUmError(null), 3000);
      return;
    }
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      await userService.delete(u.id);
      setUmSuccess('User deleted');
      loadUsers();
      setTimeout(() => setUmSuccess(null), 3000);
    } catch {
      setUmError('Failed to delete user');
      setTimeout(() => setUmError(null), 3000);
    }
  };

  const openActivityDialog = async (u: User) => {
    setActivityUser(u);
    setActivities([]);
    setActivityPage(0);
    setActivityOpen(true);
    setActivityLoading(true);
    try {
      const res = await userActivityService.getUserActivity(u.id, { limit: activityRows, offset: 0 });
      setActivities(res.activities);
      setActivitiesTotal(res.total);
    } catch {
      /* ignore */
    } finally {
      setActivityLoading(false);
    }
  };

  const loadActivity = async (userId: number, page: number, rows: number) => {
    setActivityLoading(true);
    try {
      const res = await userActivityService.getUserActivity(userId, { limit: rows, offset: page * rows });
      setActivities(res.activities);
      setActivitiesTotal(res.total);
    } finally {
      setActivityLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Settings
      </Typography>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: 'background.paper' }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="My Profile" />
          {isAdmin && <Tab icon={<PeopleIcon />} iconPosition="start" label="User Management" />}
        </Tabs>

        {/* ── Profile Tab ─────────────────────────────────────────────────── */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
            <Grid container spacing={4}>
              {/* Avatar + basic info */}
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                {/* Hidden file input */}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  onChange={handleAvatarChange}
                />
                {/* Avatar with camera overlay */}
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                  <Avatar
                    src={getAvatarUrl(currentUser?.profile_picture)}
                    sx={{ width: 100, height: 100, mx: 'auto', fontSize: '2.5rem',
                      bgcolor: ROLE_BG[currentUser?.role || 'viewer'] }}
                  >
                    {!currentUser?.profile_picture && (currentUser?.full_name?.charAt(0)?.toUpperCase() || 'U')}
                  </Avatar>
                  <Tooltip title="Change photo">
                    <IconButton
                      size="small"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={avatarUploading}
                      sx={{
                        position: 'absolute', bottom: -4, right: -4,
                        bgcolor: 'white', border: '2px solid', borderColor: 'divider',
                        width: 30, height: 30,
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                    >
                      {avatarUploading
                        ? <CircularProgress size={14} />
                        : <CameraAltIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="h6" fontWeight={700}>{currentUser?.full_name}</Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>@{currentUser?.username}</Typography>
                {currentUser?.designation && (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic" mb={1}>
                    {currentUser.designation}
                  </Typography>
                )}
                <Chip
                  label={getRoleDisplayName(currentUser?.role || '').toUpperCase()}
                  color={ROLE_COLOR[currentUser?.role || 'viewer']}
                  size="small"
                />
                {currentUser?.profile_picture && (
                  <Box mt={1}>
                    <Button size="small" color="error" onClick={handleAvatarRemove} disabled={avatarUploading}>
                      Remove photo
                    </Button>
                  </Box>
                )}
                {currentUser?.allowed_systems && currentUser.allowed_systems.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      System Access
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center">
                      {currentUser.allowed_systems.map(s => (
                        <Chip key={s} label={SYSTEM_LABELS[s] || s} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Editable fields */}
              <Grid item xs={12} md={8}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Profile Information</Typography>
                  {!profileEditing && (
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setProfileEditing(true)}>
                      Edit
                    </Button>
                  )}
                </Box>

                <Collapse in={!!profileMsg}>
                  <Alert severity={profileMsg?.type} sx={{ mb: 2 }} onClose={() => setProfileMsg(null)}>
                    {profileMsg?.text}
                  </Alert>
                </Collapse>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Full Name" value={profileData.full_name}
                      disabled={!profileEditing}
                      onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon fontSize="small" /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Email" type="email" value={profileData.email}
                      disabled={!profileEditing}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Designation" value={profileData.designation}
                      disabled={!profileEditing}
                      placeholder="e.g. Senior Technician, Factory Manager"
                      onChange={(e) => setProfileData({ ...profileData, designation: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Username" value={currentUser?.username || ''} disabled
                      helperText="Username cannot be changed" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Role" value={getRoleDisplayName(currentUser?.role || '').toUpperCase() || ''} disabled
                      helperText="Contact admin to change role" />
                  </Grid>
                </Grid>

                {profileEditing && (
                  <Box display="flex" gap={1} mt={2}>
                    <Button variant="contained" startIcon={profileSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      disabled={profileSaving} onClick={handleProfileSave}>
                      Save Changes
                    </Button>
                    <Button variant="outlined" onClick={() => {
                      setProfileEditing(false);
                      setProfileData({ full_name: currentUser?.full_name || '', email: currentUser?.email || '', designation: currentUser?.designation || '' });
                      setProfileMsg(null);
                    }}>
                      Cancel
                    </Button>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Change Password */}
                <Typography variant="h6" mb={2}>
                  <LockIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                  Change Password
                </Typography>

                <Collapse in={!!pwMsg}>
                  <Alert severity={pwMsg?.type} sx={{ mb: 2 }} onClose={() => setPwMsg(null)}>
                    {pwMsg?.text}
                  </Alert>
                </Collapse>

                <Grid container spacing={2}>
                  {[
                    { label: 'Current Password', field: 'current' as const },
                    { label: 'New Password', field: 'next' as const },
                    { label: 'Confirm New Password', field: 'confirm' as const },
                  ].map(({ label, field }) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <TextField fullWidth label={label}
                        type={showPw ? 'text' : 'password'}
                        value={pwData[field]}
                        onChange={(e) => setPwData({ ...pwData, [field]: e.target.value })}
                        InputProps={{
                          endAdornment: field === 'confirm' ? (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowPw(!showPw)}>
                                {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Box mt={2}>
                  <Button variant="contained" color="warning"
                    startIcon={pwSaving ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
                    disabled={pwSaving} onClick={handlePasswordSave}>
                    Change Password
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* ── User Management Tab ──────────────────────────────────────────── */}
        {isAdmin && (
          <TabPanel value={tab} index={1}>
            <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
              {/* Toolbar */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <TextField
                    size="small" placeholder="Search users…" value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    sx={{ width: 220 }}
                  />
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {['all', 'admin', 'manager', 'technician', 'machine', 'viewer'].map(r => (
                      <Chip key={r} label={r === 'all' ? 'All' : r.charAt(0).toUpperCase() + r.slice(1)}
                        color={roleFilter === r ? (r === 'all' ? 'primary' : ROLE_COLOR[r]) : 'default'}
                        variant={roleFilter === r ? 'filled' : 'outlined'}
                        size="small" onClick={() => setRoleFilter(r)} sx={{ cursor: 'pointer' }} />
                    ))}
                  </Stack>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                  Add User
                </Button>
              </Box>

              <Collapse in={!!umError}>
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUmError(null)}>{umError}</Alert>
              </Collapse>
              <Collapse in={!!umSuccess}>
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUmSuccess(null)}>{umSuccess}</Alert>
              </Collapse>

              <Typography variant="caption" color="text.secondary" mb={1} display="block">
                {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} shown
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="center">Role</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>System Access</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usersLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar
                              src={getAvatarUrl(u.profile_picture)}
                              sx={{ width: 34, height: 34, fontSize: '0.85rem', bgcolor: ROLE_BG[u.role] }}
                            >
                              {!u.profile_picture && u.full_name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{u.full_name}</Typography>
                              <Typography variant="caption" color="text.secondary">@{u.username}</Typography>
                              {u.designation && (
                                <Typography variant="caption" color="text.secondary" display="block">{u.designation}</Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{u.email}</Typography>
                          {u.assigned_machine && (
                            <Typography variant="caption" color="text.secondary">Machine: {u.assigned_machine}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={u.role.toUpperCase()} size="small" color={ROLE_COLOR[u.role]} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={u.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={u.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {u.role === 'admin' ? (
                            <Chip label="All Systems" size="small" color="error" variant="outlined" />
                          ) : u.allowed_systems && u.allowed_systems.length > 0 ? (
                            <Box display="flex" flexWrap="wrap" gap={0.3}>
                              {u.allowed_systems.slice(0, 2).map(s => (
                                <Chip key={s} label={SYSTEM_LABELS[s] || s} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              ))}
                              {u.allowed_systems.length > 2 && (
                                <Chip label={`+${u.allowed_systems.length - 2}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Default (role-based)</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{new Date(u.created_at).toLocaleDateString('en-GB')}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Activity Log">
                            <IconButton size="small" onClick={() => openActivityDialog(u)}><HistoryIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title={u.role === 'admin' && !isSuperAdmin ? 'Super admin only' : 'Edit'}>
                            <span>
                              <IconButton size="small" disabled={u.role === 'admin' && !isSuperAdmin}
                                onClick={() => openEditDialog(u)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={u.role === 'admin' && !isSuperAdmin ? 'Super admin only' : 'Delete'}>
                            <span>
                              <IconButton size="small" color="error"
                                disabled={u.id === currentUser?.id || (u.role === 'admin' && !isSuperAdmin)}
                                onClick={() => handleDelete(u)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        )}
      </Paper>

      {/* ── Create/Edit User Dialog ────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ bgcolor: '#00695C', color: 'white', py: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AdminIcon />
            {selectedUser ? `Edit User — ${selectedUser.username}` : 'Create New User'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Collapse in={!!formError}>
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>{formError}</Alert>
          </Collapse>
          <Collapse in={!!formSuccess}>
            <Alert severity="success" sx={{ mb: 2 }}>{formSuccess}</Alert>
          </Collapse>

          {/* Account Info */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, mt: 1, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
            Account Information
          </Typography>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Username" required value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                helperText="Used for login" disabled={!!selectedUser} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Full Name" required value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" type="email" required value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Designation" value={formData.designation || ''}
                placeholder="e.g. Senior Technician, Factory Manager"
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Password" required={!selectedUser}
                type={showPassword ? 'text' : 'password'} value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                helperText={selectedUser ? 'Leave blank to keep current password' : 'Minimum 6 characters'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }} />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Role & Access */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
            Role & Permissions
          </Typography>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={formData.role === 'machine' ? 6 : 6}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select value={formData.role} label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as any, allowed_systems: [] })}>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="manager">System manager</MenuItem>
                  <MenuItem value="technician">Technician</MenuItem>
                  <MenuItem value="machine">Machine Operator</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
                <FormHelperText>{getRoleDescription(formData.role || 'viewer')}</FormHelperText>
              </FormControl>
            </Grid>
            {formData.role === 'machine' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Assigned Machine</InputLabel>
                  <Select value={formData.assigned_machine || ''} label="Assigned Machine"
                    onChange={(e) => setFormData({ ...formData, assigned_machine: e.target.value })}>
                    {['R1','R4','R5','Steamer 01','Steamer 02','48-Digital','MS-Digital','Sublimation','Washer'].map(m => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Machine this operator is assigned to</FormHelperText>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={formData.is_active} label="Status"
                  onChange={(e) => setFormData({ ...formData, is_active: Number(e.target.value) })}>
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
                <FormHelperText>Inactive users cannot log in</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Email Report Notifications — admin & manager only */}
          {(formData.role === 'admin' || formData.role === 'manager') && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                Email Report Notifications
              </Typography>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>PR Report Frequency</InputLabel>
                    <Select
                      value={formData.pr_report_frequency || 'none'}
                      label="PR Report Frequency"
                      onChange={(e) => setFormData({ ...formData, pr_report_frequency: e.target.value as any })}
                    >
                      <MenuItem value="none">Not needed</MenuItem>
                      <MenuItem value="daily">Daily (every day at 9:30 AM)</MenuItem>
                      <MenuItem value="weekly">Weekly (every Monday at 9:30 AM)</MenuItem>
                    </Select>
                    <FormHelperText>
                      Receive an email with overdue PR spare-parts alerts
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Stock Report Frequency</InputLabel>
                    <Select
                      value={formData.stock_report_frequency || 'none'}
                      label="Stock Report Frequency"
                      onChange={(e) => setFormData({ ...formData, stock_report_frequency: e.target.value as any })}
                    >
                      <MenuItem value="none">Not needed</MenuItem>
                      <MenuItem value="daily">Daily (every day at 9:00 AM)</MenuItem>
                      <MenuItem value="weekly">Weekly (every Monday at 9:00 AM)</MenuItem>
                    </Select>
                    <FormHelperText>
                      Receive an email with low / out-of-stock spare-parts report
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {/* Allowed Systems */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
            System Access Restriction <Typography component="span" variant="caption">(optional)</Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Leave empty to allow all systems for this role. Select specific systems to restrict access.
          </Typography>

          {formData.role === 'admin' ? (
            <Alert severity="info">Admins always have full access to all systems — no restriction applies.</Alert>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {ALL_SYSTEMS.map(sysId => {
                const selected = (formData.allowed_systems || []).includes(sysId);
                return (
                  <Chip
                    key={sysId}
                    label={SYSTEM_LABELS[sysId]}
                    icon={selected ? <CheckBox fontSize="small" /> : <CheckBoxOutlineBlank fontSize="small" />}
                    onClick={() => {
                      const current = formData.allowed_systems || [];
                      setFormData({
                        ...formData,
                        allowed_systems: selected ? current.filter(s => s !== sysId) : [...current, sysId],
                      });
                    }}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={!!formSuccess}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSave}
            disabled={formSaving || !!formSuccess}
            startIcon={formSaving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {selectedUser ? 'Save Changes' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Activity Dialog ────────────────────────────────────────────────── */}
      <Dialog open={activityOpen} onClose={() => setActivityOpen(false)} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle>
          Activity Log — {activityUser?.username}
          <Typography variant="caption" display="block" color="text.secondary">
            {activityUser?.full_name} · {getRoleDisplayName(activityUser?.role || '')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {activityLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : activities.length === 0 ? (
            <Box py={4} textAlign="center"><Typography color="text.secondary">No activity recorded</Typography></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Module</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>IP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activities.map(a => (
                    <TableRow key={a.id} hover>
                      <TableCell><Typography variant="caption">{new Date(a.created_at).toLocaleString('en-GB')}</Typography></TableCell>
                      <TableCell>
                        <Chip label={a.action_type.toUpperCase()} size="small"
                          color={{ login: 'success', create: 'info', update: 'warning', delete: 'error', view: 'default' }[a.action_type] as any || 'default'} />
                      </TableCell>
                      <TableCell>{a.module}</TableCell>
                      <TableCell>{a.description}</TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{a.ip_address}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination component="div" count={activitiesTotal} page={activityPage}
                onPageChange={(_, p) => { setActivityPage(p); loadActivity(activityUser!.id, p, activityRows); }}
                rowsPerPage={activityRows}
                onRowsPerPageChange={(e) => { const r = parseInt(e.target.value, 10); setActivityRows(r); setActivityPage(0); loadActivity(activityUser!.id, 0, r); }}
                rowsPerPageOptions={[10, 25, 50, 100]} />
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
